import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { Products } from "./components/Products";
import { Memberships } from "./components/Memberships";
import { Consulting } from "./components/Consulting";
import { About } from "./components/About";
import { WhyDriveHR } from "./components/WhyDriveHR";
import { LaunchBanner } from "./components/LaunchBanner";
import { FAQ } from "./components/FAQ";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { MemberPortal } from "./pages/MemberPortal";
import { HandbookBuilderAccess } from "./components/HandbookBuilderAccess";
import { HandbookBuilderPurchase } from "./pages/HandbookBuilderPurchase";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { TermsOfService } from "./pages/TermsOfService";
import { CookiePolicy } from "./pages/CookiePolicy";
import { Disclaimer } from "./pages/Disclaimer";
import { Careers } from "./pages/Careers";
import { BundleHiring } from "./pages/BundleHiring";
import { BundlePolicies } from "./pages/BundlePolicies";
import { BundlePerformance } from "./pages/BundlePerformance";
import { BundleBenefits } from "./pages/BundleBenefits";
import { BundleTermination } from "./pages/BundleTermination";
import { BundleCompliance } from "./pages/BundleCompliance";
import { BundleCulture } from "./pages/BundleCulture";
import { BundleOperations } from "./pages/BundleOperations";
import { BundleUltimate } from "./pages/BundleUltimate";
import { AuthProvider } from "./components/AuthProvider";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { LoginPageRoute, SignupPageRoute, ForgotPasswordRoute, ResetPasswordRoute } from "./pages/AuthPages";
import { CheckoutPage, SubscriptionSuccessPage, SubscriptionCanceledPage, AccountBillingPage } from "./pages/CheckoutPages";
import { PaymentSuccessPage, PaymentCanceledPage } from "./pages/PaymentPages";
import { AdminPanel } from "./pages/AdminPanel";
import { TemplateLibraryPage, ComplianceAlertsPage, AskDriveHRPage, DocumentReviewPage } from "./pages/PortalGatedPages";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import { LaunchGate } from "./components/LaunchGate";
import { HANDBOOK_ENABLED } from "./lib/featureFlags";
import { Toaster } from "./components/ui/sonner";
import { initMetaPixelGate } from "./lib/metaPixel";

// Scroll to anchor component with robust retry mechanism
const ScrollToAnchor = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.substring(1);

      // Re-scroll on a schedule because some sections (e.g. Products) finish
      // loading asynchronously and reflow the page — a single scrollIntoView
      // at t=50ms lands the viewport above the actual target. Re-trying over
      // ~2s catches reflows from late-loading data.
      const delays = [50, 200, 600, 1200, 2000];
      const lastPositions = { y: -1 };
      const timeouts = delays.map((d) =>
        setTimeout(() => {
          const el = document.getElementById(targetId);
          if (!el) return;
          const y = Math.floor(el.getBoundingClientRect().top + window.scrollY);
          if (y === lastPositions.y) return; // no reflow → skip
          lastPositions.y = y;
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, d)
      );
      return () => timeouts.forEach(clearTimeout);
    }
    // Scroll to top if no hash
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  return null;
};

const LandingPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <LaunchBanner />
      <Hero />
      <Products />
      <Memberships />
      <Consulting />
      <About />
      <WhyDriveHR />
      <FAQ />
      <Contact />
      <Footer />
    </div>
  );
};

function App() {
  useEffect(() => {
    // Wire the Meta Pixel to the consent store. Idempotent + no-op when
    // REACT_APP_META_PIXEL_ID isn't configured for this environment.
    initMetaPixelGate();
  }, []);
  return (
    <div className="App">
      <LaunchGate>
        <BrowserRouter>
        <AuthProvider>
          <ScrollToAnchor />
          <CookieConsentBanner />
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPageRoute />} />
            <Route path="/signup" element={<SignupPageRoute />} />
            <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
            <Route path="/reset-password" element={<ResetPasswordRoute />} />
            <Route path="/members" element={<ProtectedRoute><MemberPortal /></ProtectedRoute>} />
            <Route path="/members/library" element={<ProtectedRoute><TemplateLibraryPage /></ProtectedRoute>} />
            <Route path="/members/compliance" element={<ProtectedRoute><ComplianceAlertsPage /></ProtectedRoute>} />
            <Route path="/members/ask" element={<ProtectedRoute><AskDriveHRPage /></ProtectedRoute>} />
            <Route path="/members/document-review" element={<ProtectedRoute><DocumentReviewPage /></ProtectedRoute>} />
            <Route path="/members/account" element={<ProtectedRoute><AccountBillingPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
            <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
            <Route path="/subscription-canceled" element={<SubscriptionCanceledPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-canceled" element={<PaymentCanceledPage />} />
            <Route
              path="/handbook-builder"
              element={HANDBOOK_ENABLED ? <HandbookBuilderAccess /> : <Navigate to="/" replace />}
            />
            <Route
              path="/handbook-builder-purchase"
              element={HANDBOOK_ENABLED ? <HandbookBuilderPurchase /> : <Navigate to="/" replace />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/bundle/hiring-onboarding" element={<BundleHiring />} />
            <Route path="/bundle/policies" element={<BundlePolicies />} />
            <Route path="/bundle/performance-development" element={<BundlePerformance />} />
            <Route path="/bundle/benefits-compensation" element={<BundleBenefits />} />
            <Route path="/bundle/termination-offboarding" element={<BundleTermination />} />
            <Route path="/bundle/compliance-legal" element={<BundleCompliance />} />
            <Route path="/bundle/culture-dei-engagement" element={<BundleCulture />} />
            <Route path="/bundle/hr-operations" element={<BundleOperations />} />
            <Route path="/bundle/ultimate-hr-bundle" element={<BundleUltimate />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </LaunchGate>
    </div>
  );
}

export default App;
